import React from "react";

type IconShellProps = {
    outerClassName: string;
    insetClassName: string;
    children: React.ReactNode;
};

/** Outer clip box + leaf inset — matches Figma icon geometry */
const IconShell = ({ outerClassName, insetClassName, children }: IconShellProps) => (
    <span className={`relative inline-block shrink-0 overflow-hidden ${outerClassName}`} aria-hidden>
        <span className={`absolute ${insetClassName}`}>{children}</span>
    </span>
);

/** chart-spline — outer 36×36, leaf inset 12.5% */
export const ChartSplineIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[12.5%]">
        <svg className="block size-full" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1 1V25C1 25.7956 1.31607 26.5587 1.87868 27.1213C2.44129 27.6839 3.20435 28 4 28H28M7 20.5C7.75 17.5 9.25 10 13 10C16 10 16 14.5 19 14.5C22.75 14.5 25.75 7 26.5 4"
                stroke="#4C7BE5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** building-2 — outer 36×36, leaf inset 8.33% */
export const Building2Icon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[8.33%]">
        <svg className="block size-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7 31V4C7 3.20435 7.31607 2.44129 7.87868 1.87868C8.44129 1.31607 9.20435 1 10 1H22C22.7956 1 23.5587 1.31607 24.1213 1.87868C24.6839 2.44129 25 3.20435 25 4V31M7 31H25M7 31H4C3.20435 31 2.44129 30.6839 1.87868 30.1213C1.31607 29.5587 1 28.7956 1 28V19C1 18.2044 1.31607 17.4413 1.87868 16.8787C2.44129 16.3161 3.20435 16 4 16H7M25 31H28C28.7956 31 29.5587 30.6839 30.1213 30.1213C30.6839 29.5587 31 28.7956 31 28V14.5C31 13.7044 30.6839 12.9413 30.1213 12.3787C29.5587 11.8161 28.7956 11.5 28 11.5H25M13 7H19M13 13H19M13 19H19M13 25H19"
                stroke="#4C7BE5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** hand-coins (blue) — outer 36×36, leaf inset ~8.33% */
export const HandCoinsBlueIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[8.33%_8.22%_8.33%_8.33%]">
        <svg
            className="block size-full"
            viewBox="0 0 32.0404 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M14.5 20.5H17.5C18.2956 20.5 19.0587 20.1839 19.6213 19.6213C20.1839 19.0587 20.5 18.2956 20.5 17.5C20.5 16.7044 20.1839 15.9413 19.6213 15.3787C19.0587 14.8161 18.2956 14.5 17.5 14.5H13C12.1 14.5 11.35 14.8 10.9 15.4L2.5 23.5M8.5 29.5L10.9 27.4C11.35 26.8 12.1 26.5 13 26.5H19C20.65 26.5 22.15 25.9 23.2 24.7L30.1 18.1C30.6788 17.553 31.0167 16.7984 31.0392 16.0024C31.0617 15.2063 30.767 14.4338 30.22 13.855C29.673 13.2762 28.9184 12.9383 28.1224 12.9158C27.3263 12.8933 26.5538 13.188 25.975 13.735L19.675 19.585M1 22L10 31M26.35 11.5C26.35 13.9024 24.4024 15.85 22 15.85C19.5976 15.85 17.65 13.9024 17.65 11.5C17.65 9.09756 19.5976 7.15 22 7.15C24.4024 7.15 26.35 9.09756 26.35 11.5ZM11.5 5.5C11.5 7.98528 9.48528 10 7 10C4.51472 10 2.5 7.98528 2.5 5.5C2.5 3.01472 4.51472 1 7 1C9.48528 1 11.5 3.01472 11.5 5.5Z"
                stroke="#4C7BE5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** circle-dollar-sign — outer 36×36, leaf inset 8.33% */
export const CircleDollarSignIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[8.33%]">
        <svg className="block size-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M22 10H13C12.2044 10 11.4413 10.3161 10.8787 10.8787C10.3161 11.4413 10 12.2044 10 13C10 13.7956 10.3161 14.5587 10.8787 15.1213C11.4413 15.6839 12.2044 16 13 16H19C19.7956 16 20.5587 16.3161 21.1213 16.8787C21.6839 17.4413 22 18.2044 22 19C22 19.7956 21.6839 20.5587 21.1213 21.1213C20.5587 21.6839 19.7956 22 19 22H10M16 25V7M31 16C31 24.2843 24.2843 31 16 31C7.71573 31 1 24.2843 1 16C1 7.71573 7.71573 1 16 1C24.2843 1 31 7.71573 31 16Z"
                stroke="#4C7BE5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

type GlobeIconProps = {
    /** Desktop hero: 60×60; mobile hero: 40×40 */
    size?: "md" | "lg";
};

/** globe — outer sized per Figma; leaf inset 8.33% */
export const GlobeIcon = ({ size = "md" }: GlobeIconProps) => (
    <IconShell
        outerClassName={size === "lg" ? "size-[60px]" : "size-10"}
        insetClassName="inset-[8.33%]"
    >
        <svg className="block size-full" viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M51.5 26.5C51.5 40.3071 40.3071 51.5 26.5 51.5M51.5 26.5C51.5 12.6929 40.3071 1.5 26.5 1.5M51.5 26.5H1.5M26.5 51.5C12.6929 51.5 1.5 40.3071 1.5 26.5M26.5 51.5C20.0806 44.7596 16.5 35.8081 16.5 26.5C16.5 17.1919 20.0806 8.24038 26.5 1.5M26.5 51.5C32.9194 44.7596 36.5 35.8081 36.5 26.5C36.5 17.1919 32.9194 8.24038 26.5 1.5M1.5 26.5C1.5 12.6929 12.6929 1.5 26.5 1.5"
                stroke="#4C7BE5"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** chart-area — outer 36×36, leaf inset 12.5% (Indian 4099:47644) */
export const ChartAreaIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[12.5%]">
        <svg className="block size-full" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1 1V25C1 25.7956 1.31607 26.5587 1.87868 27.1213C2.44129 27.6839 3.20435 28 4 28H28M7 13.3105C7.00004 13.112 7.0788 12.9216 7.219 12.781L10.219 9.781C10.2887 9.71116 10.3714 9.65574 10.4626 9.61793C10.5537 9.58012 10.6513 9.56066 10.75 9.56066C10.8487 9.56066 10.9463 9.58012 11.0374 9.61793C11.1286 9.65574 11.2113 9.71116 11.281 9.781L16.219 14.719C16.2887 14.7888 16.3714 14.8443 16.4626 14.8821C16.5537 14.9199 16.6513 14.9393 16.75 14.9393C16.8487 14.9393 16.9463 14.9199 17.0374 14.8821C17.1286 14.8443 17.2113 14.7888 17.281 14.719L23.719 8.281C23.8238 8.17593 23.9574 8.10432 24.1029 8.07522C24.2485 8.04612 24.3994 8.06085 24.5365 8.11754C24.6736 8.17423 24.7909 8.27033 24.8734 8.39369C24.9559 8.51704 25 8.6621 25 8.8105V20.5C25 20.8978 24.842 21.2794 24.5607 21.5607C24.2794 21.842 23.8978 22 23.5 22H8.5C8.10218 22 7.72064 21.842 7.43934 21.5607C7.15804 21.2794 7 20.8978 7 20.5V13.3105Z"
                stroke="#F47843"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** boxes — outer 36×36, leaf inset ~9.7% / 8.33% (Indian 4099:47650) */
export const BoxesIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[9.73%_8.33%_9.71%_8.33%]">
        <svg
            className="block size-full"
            viewBox="0 0 32 31.0034"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16 25.9984L10.045 29.5684C9.57841 29.8488 9.04433 29.9969 8.5 29.9969C7.95567 29.9969 7.4216 29.8488 6.955 29.5684L2.455 26.8684C2.01214 26.6024 1.64549 26.2264 1.39058 25.777C1.13567 25.3277 1.00113 24.8201 1 24.3034V19.4434C1.00113 18.9268 1.13567 18.4192 1.39058 17.9698C1.64549 17.5204 2.01214 17.1445 2.455 16.8784L8.5 13.2484M16 25.9984V17.7484M16 25.9984L21.955 29.5684C22.4216 29.8488 22.9557 29.9969 23.5 29.9969C24.0443 29.9969 24.5784 29.8488 25.045 29.5684L29.545 26.8684C29.9879 26.6024 30.3545 26.2264 30.6094 25.777C30.8643 25.3277 30.9989 24.8201 31 24.3034V19.4434C30.9989 18.9268 30.8643 18.4192 30.6094 17.9698C30.3545 17.5204 29.9879 17.1445 29.545 16.8784L23.5 13.2484M16 17.7484L8.5 13.2484M16 17.7484L8.5 22.2484M16 17.7484L23.5 13.2484M16 17.7484L23.5 22.2484M16 17.7484V9.49843M8.5 13.2484V6.69343C8.50113 6.17679 8.63567 5.66919 8.89058 5.21981C9.14549 4.77043 9.51214 4.3945 9.955 4.12843L14.455 1.42843C14.9216 1.1481 15.4557 1 16 1C16.5443 1 17.0784 1.1481 17.545 1.42843L22.045 4.12843C22.4879 4.3945 22.8545 4.77043 23.1094 5.21981C23.3643 5.66919 23.4989 6.17679 23.5 6.69343V13.2484M8.5 22.2484L1.39 17.9734M8.5 22.2484V30.0034M23.5 22.2484L30.61 17.9734M23.5 22.2484V30.0034M16 9.49843L8.89 5.22343M16 9.49843L23.11 5.22343"
                stroke="#F47843"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

/** chart-no-axes-combined (orange) — outer 36×36 (Indian 4099:47656) */
export const ChartNoAxesCombinedOrangeIcon = () => (
    <IconShell outerClassName="size-9" insetClassName="inset-[12.5%_8.33%]">
        <svg className="block size-full" viewBox="0 0 32 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M16 20.5V28M22 17.5V28M28 11.5V28M31 1L18.031 13.969C17.9613 14.0388 17.8786 14.0943 17.7874 14.1321C17.6963 14.1699 17.5987 14.1893 17.5 14.1893C17.4013 14.1893 17.3037 14.1699 17.2126 14.1321C17.1214 14.0943 17.0387 14.0388 16.969 13.969L12.031 9.031C11.8904 8.8904 11.6996 8.81141 11.5007 8.81141C11.3019 8.81141 11.1111 8.8904 10.9705 9.031L1 19M4 23.5V28M10 17.5V28"
                stroke="#F47843"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    </IconShell>
);

type CurrencyInrIconProps = {
    /** Feature row: 36×36; hero: 40×40 mobile / 60×60 desktop */
    size?: "sm" | "md" | "lg";
};

/** CurrencyInr — leaf inset top 12.5% / bottom 9.35% / left 25% / right 18.75% */
export const CurrencyInrIcon = ({ size = "sm" }: CurrencyInrIconProps) => {
    const outer =
        size === "lg" ? "size-[60px]" : size === "md" ? "size-10" : "size-9";
    const strokeWidth = size === "sm" ? "2" : "3";
    const viewBox = size === "sm" ? "0 0 20.25 28.1341" : "0 0 33.75 46.8901";

    return (
        <IconShell
            outerClassName={outer}
            insetClassName="bottom-[9.35%] left-1/4 right-[18.75%] top-[12.5%]"
        >
            {size === "sm" ? (
                <svg
                    className="block size-full"
                    viewBox={viewBox}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.125 1H19.125C19.1582 1 19.1904 1.01269 19.2139 1.03613C19.2373 1.05957 19.25 1.09185 19.25 1.125C19.25 1.15815 19.2373 1.19043 19.2139 1.21387C19.1904 1.23731 19.1582 1.25 19.125 1.25H9.36328L11.2373 2.98438C12.1221 3.80313 12.7956 4.82407 13.1992 5.95996L13.4355 6.625H19.125C19.1582 6.625 19.1904 6.63769 19.2139 6.66113C19.2373 6.68457 19.25 6.71685 19.25 6.75C19.25 6.78315 19.2373 6.81543 19.2139 6.83887C19.1904 6.86231 19.1582 6.875 19.125 6.875H13.5332L13.6064 7.94336C13.6181 8.11388 13.625 8.27702 13.625 8.4375C13.6224 10.4092 12.8376 12.2991 11.4434 13.6934C10.0489 15.0878 8.15861 15.8727 6.18652 15.875H1.44824L3.3623 17.6152L13.584 26.9072L13.5928 26.916C13.6051 26.927 13.6159 26.9402 13.623 26.9551C13.63 26.9697 13.6338 26.9857 13.6348 27.002C13.6356 27.0184 13.6334 27.0353 13.6279 27.0508C13.6224 27.0663 13.6136 27.0806 13.6025 27.0928C13.5914 27.105 13.5775 27.1151 13.5625 27.1221C13.5477 27.1289 13.5319 27.1331 13.5156 27.1338C13.4991 27.1345 13.4823 27.1317 13.4668 27.126C13.4513 27.1202 13.4369 27.1118 13.4248 27.1006L13.416 27.0928L1.04102 15.8428L1.01758 15.8135C1.01122 15.8027 1.00681 15.7906 1.00391 15.7783C0.998195 15.7539 0.999745 15.7285 1.00879 15.7051C1.01788 15.6815 1.03388 15.6608 1.05469 15.6465C1.06491 15.6395 1.07622 15.6345 1.08789 15.6309L1.125 15.625H6.18848C8.09376 15.6228 9.92034 14.8648 11.2676 13.5176C12.5306 12.2546 13.2762 10.5704 13.3662 8.79492L13.375 8.43848V8.4375C13.375 8.21305 13.3629 7.9952 13.3438 7.78418L13.2617 6.875H1.125C1.09185 6.875 1.05958 6.86231 1.03613 6.83887C1.01269 6.81542 1 6.78315 1 6.75L1.00977 6.70215C1.01599 6.68717 1.02444 6.67283 1.03613 6.66113C1.05958 6.63769 1.09185 6.625 1.125 6.625H13.3291L12.5869 5.1709C11.985 3.99143 11.0685 3.0004 9.93945 2.30859C8.81055 1.61694 7.51241 1.25075 6.18848 1.25H1.125C1.09185 1.25 1.05958 1.23731 1.03613 1.21387C1.01269 1.19042 1 1.15815 1 1.125L1.00977 1.07715C1.01599 1.06217 1.02444 1.04783 1.03613 1.03613C1.04783 1.02444 1.06217 1.01599 1.07715 1.00977L1.125 1Z"
                        fill="#283A97"
                        stroke="#F47843"
                        strokeWidth={strokeWidth}
                    />
                </svg>
            ) : (
                <svg
                    className="block size-full"
                    viewBox={viewBox}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.875 1.5H31.875C31.9745 1.5 32.0703 1.53905 32.1406 1.60938C32.2109 1.6797 32.25 1.77554 32.25 1.875C32.25 1.97446 32.2109 2.0703 32.1406 2.14062C32.0703 2.21095 31.9745 2.25 31.875 2.25H16.0312L18.8418 4.85059C20.3362 6.23345 21.4735 7.95842 22.1553 9.87695L22.5098 10.875H31.875C31.9745 10.875 32.0703 10.914 32.1406 10.9844C32.2109 11.0547 32.25 11.1505 32.25 11.25C32.25 11.3495 32.2109 11.4453 32.1406 11.5156C32.0703 11.586 31.9745 11.625 31.875 11.625H22.7344L22.8438 13.2266C22.8633 13.5135 22.875 13.79 22.875 14.0625C22.8707 17.3929 21.5454 20.5854 19.1904 22.9404C16.835 25.2958 13.6416 26.6211 10.3105 26.625H2.84375L5.71484 29.2354L22.752 44.7227L22.7656 44.7344C22.8027 44.7673 22.8329 44.8078 22.8545 44.8525C22.876 44.8971 22.888 44.9457 22.8906 44.9951C22.8933 45.0445 22.8867 45.094 22.8701 45.1406C22.8535 45.1874 22.8273 45.2309 22.7939 45.2676C22.7607 45.3041 22.7205 45.3335 22.6758 45.3545C22.6308 45.3755 22.5818 45.3876 22.5322 45.3896C22.4827 45.3917 22.4332 45.3843 22.3867 45.3672C22.3402 45.35 22.2971 45.3238 22.2607 45.29L22.2549 45.2832L22.248 45.2773L1.62305 26.5273C1.56706 26.4764 1.52815 26.4096 1.51074 26.3359C1.49342 26.2625 1.49826 26.1857 1.52539 26.1152C1.55267 26.0446 1.60066 25.9832 1.66309 25.9404C1.71002 25.9083 1.76355 25.8873 1.81934 25.8789L1.875 25.875H10.3145C13.4457 25.8714 16.448 24.6262 18.6621 22.4121C20.738 20.3363 21.9616 17.5676 22.1094 14.6494L22.125 14.0645V14.0625C22.125 13.6942 22.1058 13.3367 22.0742 12.9893L21.9502 11.625H1.875C1.77554 11.625 1.6797 11.586 1.60938 11.5156C1.53905 11.4453 1.5 11.3495 1.5 11.25L1.50684 11.1768C1.5212 11.1046 1.55655 11.0372 1.60938 10.9844C1.6797 10.914 1.77554 10.875 1.875 10.875H21.9424L20.8291 8.69336C19.8399 6.75493 18.3341 5.1272 16.4785 3.99023C14.623 2.85339 12.4896 2.25112 10.3135 2.25H1.875C1.77554 2.25 1.6797 2.21095 1.60938 2.14062C1.53905 2.0703 1.5 1.97446 1.5 1.875L1.50684 1.80176C1.5212 1.72955 1.55655 1.6622 1.60938 1.60938C1.6622 1.55655 1.72955 1.5212 1.80176 1.50684L1.875 1.5Z"
                        fill="#283A97"
                        stroke="#F47843"
                        strokeWidth={strokeWidth}
                    />
                </svg>
            )}
        </IconShell>
    );
};
